/**
 * Normalizes Projects in Artwork list API payloads:
 * - New shape: `vendorList.objects[].vendorAssessment.objects[]` (per-version rows).
 * - Legacy: `vendorDetails[]` (unchanged, light status normalization).
 */

import type {
  ProjectsInArtworkListItem,
  ProjectsInArtworkListMeta,
  ProjectsInArtworkVendorDetail,
} from "@/services/artworkService";

export type { ProjectsInArtworkListMeta as ArtworkListMeta };

export const ARTWORK_LIST_ROW_KIND_PRODUCT = "artwork_product" as const;
export const ARTWORK_LIST_ROW_KIND_VERSION = "artwork_version" as const;

export type ArtworkListDisplayRowKind =
  | typeof ARTWORK_LIST_ROW_KIND_PRODUCT
  | typeof ARTWORK_LIST_ROW_KIND_VERSION;

/** `id` prefixes so DataGrid row ids never collide with numeric product ids. */
export function artworkListParentRowId(productId: string | number): string {
  return `artwork-row-p-${String(productId)}`;
}

export function artworkListVersionRowId(productId: string | number, vendorAssessmentId: string | number): string {
  return `artwork-row-v-${String(productId)}-${String(vendorAssessmentId)}`;
}

export function unwrapCountedObjects<T = unknown>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw != null && typeof raw === "object" && Array.isArray((raw as { objects?: unknown }).objects)) {
    return ((raw as { objects: T[] }).objects ?? []) as T[];
  }
  return [];
}

export function hasAnyVendorWithMultipleVersionsFromVendorList(raw: unknown): boolean {
  const vendors = unwrapCountedObjects<Record<string, unknown>>(raw);
  return vendors.some((v) => {
    const assessments = unwrapCountedObjects(v.vendorAssessment);
    return assessments.length > 1;
  });
}

function flattenVendorListToVendorDetails(
  raw: Record<string, unknown>
): ProjectsInArtworkVendorDetail[] {
  const vendors = unwrapCountedObjects<Record<string, unknown>>(raw.vendorList);
  const out: ProjectsInArtworkVendorDetail[] = [];

  for (const vendor of vendors) {
    const vendorId = vendor.vendorId ?? vendor.id;
    const vendorIdStr = vendorId != null && String(vendorId).trim() !== "" ? String(vendorId).trim() : "";
    const name = String(vendor.vendorCorporateName ?? vendor.vendorName ?? vendor.name ?? "").trim();
    const assessments = unwrapCountedObjects<Record<string, unknown>>(vendor.vendorAssessment);

    for (const a of assessments) {
      const aid = a.vendorAssessmentId ?? a.assessmentId;
      const aidStr = aid != null && String(aid).trim() !== "" ? String(aid).trim() : "";
      if (!aidStr) continue;

      const artworkStatus = (a.artworkStatus ?? a.status ?? null) as string | null;
      const projectId =
        a.projectId != null && String(a.projectId).trim() !== ""
          ? String(a.projectId).trim()
          : null;
      const deadline = (a.artWorkDeadline ?? a.artworkDeadline ?? null) as string | null;
      const legacyIncomplete =
        a.legacyIncomplete != null && String(a.legacyIncomplete).trim() !== ""
          ? String(a.legacyIncomplete).trim()
          : null;

      out.push({
        id: vendorIdStr || aidStr,
        vendorId: vendorIdStr,
        vendorCorporateName: name || `Vendor ${aidStr}`,
        artworkStatus,
        status: artworkStatus,
        artworkProjectId: projectId,
        artWorkDeadline: deadline,
        vendorAssessmentId: aidStr,
        assessmentId: aidStr,
        versionName: a.versionName != null ? String(a.versionName) : null,
        businessStatus: a.businessStatus != null ? String(a.businessStatus) : null,
        workflowStatus: a.workflowStatus,
        goldNumber: vendor.goldNumber != null ? String(vendor.goldNumber) : null,
        legacyIncomplete,
      } as ProjectsInArtworkVendorDetail);
    }
  }

  return out;
}

function buildArtworkListMeta(vendorDetails: ProjectsInArtworkVendorDetail[]): ProjectsInArtworkListMeta {
  const vendorIds = new Set(
    vendorDetails
      .map((v) => String((v as { vendorId?: string }).vendorId ?? v.id ?? "").trim())
      .filter(Boolean)
  );
  const byVendor = new Map<string, number>();
  for (const v of vendorDetails) {
    const vid = String((v as { vendorId?: string }).vendorId ?? v.id ?? "").trim();
    if (!vid) continue;
    byVendor.set(vid, (byVendor.get(vid) ?? 0) + 1);
  }
  const hasAnyVendorMultiVersion = [...byVendor.values()].some((c) => c > 1);
  return {
    uniqueVendorCount: vendorIds.size,
    totalVersionCount: vendorDetails.length,
    hasAnyVendorMultiVersion,
  };
}

/** Multiple flattened vendor/version lines → expandable parent + child rows in the list grid. */
export function artworkListItemHasAccordionParent(
  row: Pick<ProjectsInArtworkListItem, "vendorDetails">
): boolean {
  return Array.isArray(row.vendorDetails) && row.vendorDetails.length > 1;
}

/**
 * Normalize a single list row from API (new `vendorList` or legacy `vendorDetails`).
 */
export function normalizeProjectsInArtworkListItem(raw: unknown): ProjectsInArtworkListItem {
  const row = (raw != null && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const id = row.id != null ? String(row.id).trim() : "";

  const fromVendorList = unwrapCountedObjects(row.vendorList).length > 0;
  let vendorDetails: ProjectsInArtworkVendorDetail[] = [];

  if (fromVendorList) {
    vendorDetails = flattenVendorListToVendorDetails(row);
  } else if (Array.isArray(row.vendorDetails)) {
    vendorDetails = (row.vendorDetails as ProjectsInArtworkVendorDetail[]).map((v) => ({
      ...v,
      status: (v as { status?: string }).status ?? (v as { artworkStatus?: string }).artworkStatus ?? null,
      vendorAssessmentId:
        (v as { vendorAssessmentId?: string | number }).vendorAssessmentId != null
          ? String((v as { vendorAssessmentId?: string | number }).vendorAssessmentId)
          : (v as { assessmentId?: string | number }).assessmentId != null
            ? String((v as { assessmentId?: string | number }).assessmentId)
            : undefined,
    })) as ProjectsInArtworkVendorDetail[];
  }

  const first = vendorDetails[0];
  const artworkStatuses =
    (row.artworkStatuses as string | null | undefined) ??
    (first?.artworkStatus ?? first?.status ?? null) ??
    null;
  const artworkDeadline =
    (row.artworkDeadline as string | null | undefined) ??
    (first?.artWorkDeadline ?? (first as { artworkDeadline?: string })?.artworkDeadline ?? null) ??
    null;

  const artworkListMeta = buildArtworkListMeta(vendorDetails);

  return {
    ...(row as object),
    id,
    sku: row.sku != null ? String(row.sku) : (row as ProjectsInArtworkListItem).sku,
    vendorDetails,
    artworkStatuses: artworkStatuses != null ? String(artworkStatuses) : null,
    artworkDeadline: artworkDeadline != null ? String(artworkDeadline) : null,
    artworkListMeta,
  } as ProjectsInArtworkListItem;
}

export function normalizeProjectsInArtworkListResponse(rows: unknown[]): ProjectsInArtworkListItem[] {
  return (Array.isArray(rows) ? rows : []).map((r) => normalizeProjectsInArtworkListItem(r));
}
